/* ----------------------------------------------------------------------------
 * HttpSttAdapter — provider-neutral RECORD-AND-TRANSCRIBE STT over the server
 * proxy. NO provider key OR token in the browser, ever.
 *
 * FLOW (server-proxy, no ephemeral token):
 *   1. openStream(): start a MediaRecorder on the mic MediaStream (tap-to-talk).
 *   2. finish(): stop recording, assemble the utterance Blob, and POST it to
 *      POST /v1/ai/voice/stt (same-origin, session cookie + CSRF only). The
 *      server injects the provider key server-side and returns the transcript,
 *      which is emitted via onFinal.
 *   3. close(): abort capture without transcribing (hard stop).
 * The browser only ever sends audio (+ cookie/CSRF) and receives text — it never
 * holds a Deepgram key, a token, a project id, or opens a provider WebSocket.
 * Implements the same SttAdapter contract as the mock.
 *
 * SHORT-UTTERANCE CAPTURE FIX (2026-07-19, Lane E defect A) + FIRST-WORD
 * TRUNCATION FIX (2026-07-20, Lane STT-CAPTURE). A quick hold on mobile used to
 * end before the MediaRecorder timeslice fired, so stop() flushed a
 * 0-byte/header-only blob → empty transcript. A timeslice ALSO fragmented iOS
 * Safari `audio/mp4` into non-concatenable pieces, so a multi-word phrase
 * transcribed as only its FIRST word. This adapter now:
 *   - records with NO timeslice, emitting the whole utterance as ONE finalized
 *     container (decodable on iOS mp4 AND desktop webm — no fragment concat),
 *   - stops WITHOUT a requestData() pre-flush: with no timeslice, stop() alone
 *     flushes the whole buffer as ONE finalized container. An extra requestData()
 *     flush on a no-timeslice recorder made the WebM muxer write a bogus ~1ms
 *     Segment duration (+ a stray Cues tail), degrading Deepgram accuracy while
 *     still capturing the full phrase — so it is removed, and
 *   - enforces a MINIMUM-CAPTURE FLOOR (minCaptureMs) so an ultra-short tap keeps
 *     the mic open just long enough to yield a transcribable clip.
 * The floor is honoured cooperatively with close(): a Stop during the floor wait
 * aborts immediately and discards (never sends). This is a GENERAL capture fix —
 * not a phrase allowlist. A residual empty transcript is still handed back so the
 * controller can surface a general re-prompt rather than a silent drop.
 * -------------------------------------------------------------------------- */

import type { SttAdapter, SttOpenOptions, SttResultMeta, SttStream, SttStreamHandlers } from '../stt/SttAdapter.ts';
import { transcribeStt, VoiceUnavailableError } from './voiceApi.ts';

/** First MediaRecorder-supported container; undefined => let the browser pick. */
function pickMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  const MR = (globalThis as unknown as { MediaRecorder?: { isTypeSupported?: (t: string) => boolean } }).MediaRecorder;
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const c of candidates) {
      try {
        if (MR.isTypeSupported(c)) return c;
      } catch {
        /* keep trying */
      }
    }
  }
  return undefined;
}

/** Default minimum time (ms) the mic stays open before finish() actually stops
 *  it, so an ultra-short tap still captures a transcribable clip instead of a
 *  0-byte/header-only blob. Overridable (tests pass 0 for deterministic timing). */
const DEFAULT_MIN_CAPTURE_MS = 300;

/** Tail-hold (ms): keep the recorder LIVE this long AFTER finish()/release is
 *  called, and only THEN stop() it. This is the trailing MIRROR of the leading
 *  capture-ready warm-up gate (startRecorderAndAwaitCapture): just as we wait for
 *  the mic to become live BEFORE inviting speech so no LEADING word is lost, we
 *  hold the mic open a beat AFTER release so the TRAILING word + encoder-pipeline
 *  tail is recorded before stop(). Browsers that do NOT flush the last
 *  ~200-400ms on a no-flush stop() (notably Firefox desktop) would otherwise clip
 *  the final word. The hold is RELEASE-relative and composes with the
 *  START-relative min-capture floor: the recorder stops at
 *  max(floorRemaining, tailHold). It is cancelable by close()/abort via the SAME
 *  timer as the floor, so a Stop during the hold resolves immediately (never
 *  hangs). This adds ~tailHold latency ONLY on release (the finalize path) — zero
 *  added latency at start, during speech, or on the response. NB: it does NOT
 *  reintroduce a timeslice or a requestData() pre-flush (those wrote a bogus ~1ms
 *  container and hurt Deepgram accuracy) — stop() still flushes ONE finalized
 *  container. Overridable (tests pass 0 for deterministic timing). */
const DEFAULT_TAIL_HOLD_MS = 300;

/** Safety-net ceiling (ms) for waiting on the recorder's `start` event before we
 *  let the caller show "Listening". `start` normally fires within tens of ms;
 *  this ONLY prevents a permanent hang if a browser never dispatches it. */
const DEFAULT_CAPTURE_READY_TIMEOUT_MS = 4000;

/** A provider STT confidence below this (when reported, i.e. > 0) is treated as
 *  "did not catch it": the adapter hands back an EMPTY transcript so the
 *  controller surfaces the ask-to-repeat re-prompt rather than acting on a likely
 *  mis-hear (owner directive 2026-07-22, revision). 0 == not reported. */
const LOW_STT_CONFIDENCE = 0.35;

/** Start the recorder and RESOLVE only once capture is genuinely LIVE - i.e. the
 *  recorder has fired `start` (the UA has ACTUALLY begun recording), not merely
 *  that start() was CALLED. On mobile (especially a first-permission grant)
 *  start() returns while the mic pipeline is still warming up; resolving then
 *  made the caller show "Listening" and the user speak into a not-yet-capturing
 *  recorder, dropping the LEADING words (leading-audio/first-word truncation,
 *  2026-07-22). With NO timeslice `ondataavailable` never fires before stop(), so
 *  `start` is the correct capture-live signal. A bounded safety net guarantees we
 *  NEVER hang if `start` is not dispatched. */
function startRecorderAndAwaitCapture(recorder: MediaRecorder, timeoutMs: number): Promise<number> {
  return new Promise<number>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Measure recorder-start latency (start() call -> `start` event) as a proxy
    // for mic warm-up / leading-audio-dropped. DIAGNOSTIC ONLY: it rides the gated
    // beacon and never gates or changes capture behaviour.
    let startCalledAt = 0;
    const done = (): void => {
      if (settled) return;
      settled = true;
      if (timer !== null) { clearTimeout(timer); timer = null; }
      try { recorder.onstart = null; } catch { /* no-op */ }
      resolve(startCalledAt > 0 ? Date.now() - startCalledAt : 0);
    };
    // `start` = the UA has actually begun capturing (the ONLY point the caller
    // may show "Listening" / invite speech). Set BEFORE start() so a synchronous
    // dispatch is not missed.
    try { recorder.onstart = () => done(); } catch { /* no-op */ }
    try {
      startCalledAt = Date.now();
      recorder.start(); // NO timeslice - one finalized container flushed on stop().
    } catch {
      done(); // a start() throw surfaces later as an empty capture; never hang.
      return;
    }
    if (!settled && timeoutMs > 0) {
      // Safety net ONLY: proceed after a bounded wait even if `start` never
      // fires - best-effort, never a permanent hang.
      timer = setTimeout(done, timeoutMs);
    } else if (!settled) {
      // timeoutMs<=0 (deterministic tests): resolve on the next microtask if the
      // recorder is already recording, so a test never hangs without a `start`.
      queueMicrotask(() => { if (recorder.state !== "inactive") done(); });
    }
  });
}

/** One per-CAPTURE correlation id (one per utterance). Forwarded to the backend
 *  STT upload as X-BC-Voice-Cid and emitted on the FE stt_result beacon so the FE
 *  capture line, the backend ffprobe record and the Deepgram stt.result all JOIN on
 *  ONE identical cid. Text-free id only; used solely by the gated diagnostics and
 *  it NEVER alters capture or transcription behaviour. */
function newCaptureCid(): string {
  try {
    const c = (globalThis as unknown as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return 'cap_' + c.randomUUID();
  } catch {
    /* no-op */
  }
  return 'cap_' + Math.random().toString(16).slice(2) + Date.now().toString(16);
}

/** COARSE surface label from the userAgent — DIAGNOSTIC LABELLING ONLY. Emitted
 *  in the gated beacon so the owner can split mobile vs desktop captures; it MUST
 *  NEVER gate capture or routing (no behaviour branches on it). */
function coarseSurface(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
  try {
    const ua = (globalThis as unknown as { navigator?: { userAgent?: string } }).navigator?.userAgent;
    if (typeof ua !== 'string' || ua === '') return 'unknown';
    if (/\b(iPad|Tablet|PlayBook|Silk)\b/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
    if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile';
    return 'desktop';
  } catch {
    return 'unknown';
  }
}

/** Read the CAPTURE PARAMETERS at capture time for the GATED FE diagnostics
 *  beacon. Text-free (sizes/flags/rates only). Every field is best-effort: a
 *  value the browser does not expose in THIS code path is OMITTED (never faked),
 *  so the beacon honestly reflects what this device+browser actually reported.
 *  DIAGNOSTIC ONLY — nothing read here alters capture or routing behaviour. */
function readCaptureProfile(
  stream: MediaStream,
  recorder: MediaRecorder,
  startLatencyMs: number,
): Partial<SttResultMeta> {
  const p: Partial<SttResultMeta> = {};
  try {
    const track = typeof stream.getAudioTracks === 'function' ? stream.getAudioTracks()[0] : undefined;
    const s: MediaTrackSettings | undefined =
      track && typeof track.getSettings === 'function' ? track.getSettings() : undefined;
    if (s) {
      if (typeof s.sampleRate === 'number') p.sampleRate = s.sampleRate;
      if (typeof s.channelCount === 'number') p.channelCount = s.channelCount;
      if (typeof s.echoCancellation === 'boolean') p.echoCancellation = s.echoCancellation;
      if (typeof s.autoGainControl === 'boolean') p.autoGainControl = s.autoGainControl;
      if (typeof s.noiseSuppression === 'boolean') p.noiseSuppression = s.noiseSuppression;
      // `latency` is not in every TS DOM lib's MediaTrackSettings — read it via a
      // widened view so we surface it WHEN the UA exposes it, without a type error.
      const lat = (s as unknown as { latency?: unknown }).latency;
      if (typeof lat === 'number') p.latency = lat;
    }
    // REQUESTED tier: MediaStreamTrack.getConstraints() — what getUserMedia was
    // ASKED for (DISTINCT from getSettings() ACTUAL). Empty for a bare
    // `{audio:true}` request, in which case every req_* is OMITTED (never faked).
    try {
      const gc = track as unknown as { getConstraints?: () => MediaTrackConstraints };
      const c: MediaTrackConstraints | undefined =
        track && typeof gc.getConstraints === 'function' ? gc.getConstraints() : undefined;
      if (c) {
        const asNum = (v: unknown): number | undefined => {
          if (typeof v === 'number') return v;
          if (v && typeof v === 'object') {
            const o = v as { ideal?: unknown; exact?: unknown };
            if (typeof o.ideal === 'number') return o.ideal;
            if (typeof o.exact === 'number') return o.exact;
          }
          return undefined;
        };
        const asBool = (v: unknown): boolean | undefined => {
          if (typeof v === 'boolean') return v;
          if (v && typeof v === 'object') {
            const o = v as { ideal?: unknown; exact?: unknown };
            if (typeof o.ideal === 'boolean') return o.ideal;
            if (typeof o.exact === 'boolean') return o.exact;
          }
          return undefined;
        };
        const cc = c as unknown as Record<string, unknown>;
        const rSr = asNum(cc.sampleRate);
        if (rSr !== undefined) p.reqSampleRate = rSr;
        const rCh = asNum(cc.channelCount);
        if (rCh !== undefined) p.reqChannelCount = rCh;
        const rEc = asBool(cc.echoCancellation);
        if (rEc !== undefined) p.reqEchoCancellation = rEc;
        const rAgc = asBool(cc.autoGainControl);
        if (rAgc !== undefined) p.reqAutoGainControl = rAgc;
        const rNs = asBool(cc.noiseSuppression);
        if (rNs !== undefined) p.reqNoiseSuppression = rNs;
        const rLat = asNum(cc.latency);
        if (rLat !== undefined) p.reqLatency = rLat;
      }
    } catch {
      /* getConstraints unavailable — omit the requested tier, never fake it. */
    }
  } catch {
    /* getSettings unavailable on this UA — omit track params, never fake them. */
  }
  try {
    if (typeof recorder.mimeType === 'string' && recorder.mimeType !== '') p.mimeFull = recorder.mimeType;
    if (typeof recorder.audioBitsPerSecond === 'number' && recorder.audioBitsPerSecond > 0) {
      p.audioBitsPerSecond = recorder.audioBitsPerSecond;
    }
  } catch {
    /* omit bitrate/mime when the recorder does not expose them. */
  }
  p.surface = coarseSurface();
  if (typeof startLatencyMs === 'number' && startLatencyMs >= 0) p.startLatencyMs = startLatencyMs;
  return p;
}

class HttpSttStream implements SttStream {
  readonly provider: string;
  private readonly recorder: MediaRecorder;
  private readonly chunks: BlobPart[];
  private readonly mimeType: string;
  /** gated FE diagnostics: capture parameters read at capture time (text-free). */
  private readonly captureProfile: Partial<SttResultMeta>;
  private readonly handlers: SttStreamHandlers;
  /** wall-clock time the recorder started (for the minimum-capture floor). */
  private readonly startedAt: number;
  /** minimum capture window before finish() may stop the recorder. */
  private readonly minCaptureMs: number;
  /** release-relative tail-hold: keep recording this long AFTER finish() before
   *  stop(), so the trailing word + encoder tail is captured (mirror of the
   *  leading warm-up). Composed with the floor as max(floorRemaining, tailHold). */
  private readonly tailHoldMs: number;
  private finished = false;
  private closed = false;
  /** pending stop-defer timer for the min-capture floor AND the release tail-hold
   *  (whichever is longer). Cleared by close() so Stop is instant (never hangs). */
  private floorTimer: ReturnType<typeof setTimeout> | null = null;
  /** resolver for an in-flight stopAndCollect() so close() can unblock finish(). */
  private pendingCollectResolve: ((b: Blob | null) => void) | null = null;
  /** aborts an in-flight transcribeStt upload when close()/Stop fires. */
  private readonly abort = new AbortController();

  constructor(
    provider: string,
    recorder: MediaRecorder,
    chunks: BlobPart[],
    mimeType: string,
    handlers: SttStreamHandlers,
    startedAt: number,
    minCaptureMs: number,
    tailHoldMs: number,
    captureProfile: Partial<SttResultMeta> = {},
  ) {
    this.provider = provider;
    this.recorder = recorder;
    this.chunks = chunks;
    this.mimeType = mimeType;
    this.captureProfile = captureProfile;
    this.handlers = handlers;
    this.startedAt = startedAt;
    this.minCaptureMs = Math.max(0, minCaptureMs);
    this.tailHoldMs = Math.max(0, tailHoldMs);
  }

  /** Resolve the pending stopAndCollect() with the assembled blob (or null when
   *  nothing was captured). Idempotent — a second call is a no-op. */
  private resolveCollect(): void {
    const resolve = this.pendingCollectResolve;
    if (!resolve) return;
    this.pendingCollectResolve = null;
    const blob = this.chunks.length > 0 ? new Blob(this.chunks, { type: this.mimeType || 'audio/webm' }) : null;
    resolve(blob);
  }

  /** Stop the recorder with NO requestData() pre-flush, so its whole buffer
   *  flushes as ONE finalized, correctly-dated container; resolve the collected
   *  blob on the recorder's onstop (or immediately if already stopped). */
  private stopRecorder(): void {
    try {
      // NO requestData() pre-flush. With no timeslice the recorder buffers the
      // WHOLE utterance and stop() flushes it as ONE finalized, correctly-dated
      // container (webm/opus or iOS mp4) in a single `dataavailable`. Calling
      // requestData() here forced the muxer to finalize a Segment with a bogus
      // ~1ms duration (+ a stray Cues tail), which Deepgram ingested as a
      // malformed clip — the whole phrase captured but ASR accuracy dropped. The
      // min-capture floor already guards the ultra-short-tap 0-byte case, so the
      // pre-flush is redundant AND harmful (root cause, stt-accuracy-2026-07-20).
      this.recorder.onstop = () => this.resolveCollect();
      if (this.recorder.state !== 'inactive') this.recorder.stop();
      else this.resolveCollect();
    } catch {
      this.resolveCollect();
    }
  }

  /** Stop recording (after the minimum-capture floor) and resolve the recorded
   *  utterance as a Blob (null when nothing was captured). */
  private stopAndCollect(): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.pendingCollectResolve = resolve;
      // Compose the START-relative min-capture floor (protects ultra-SHORT taps
      // from a 0-byte/header-only blob) with the RELEASE-relative tail-hold
      // (protects the TRAILING word + encoder tail from a no-flush stop()). Stop
      // at whichever is later: floorRemaining is measured from capture-start, the
      // tail-hold is measured from THIS release, so max() keeps the mic open long
      // enough for BOTH guarantees. For a real utterance the floor has long since
      // elapsed, so the tail-hold governs (~tailHold added on release only).
      const floorRemaining = this.minCaptureMs - (Date.now() - this.startedAt);
      const wait = Math.max(floorRemaining, this.tailHoldMs);
      if (wait <= 0 || this.closed) {
        this.stopRecorder();
        return;
      }
      // Keep the mic open until BOTH the min-capture floor and the release
      // tail-hold are satisfied so a very short tap still records a transcribable
      // clip AND the trailing word survives. close()/Stop cancels this instantly.
      this.floorTimer = setTimeout(() => {
        this.floorTimer = null;
        if (this.closed) {
          this.resolveCollect();
          return;
        }
        this.stopRecorder();
      }, wait);
    });
  }

  async finish(): Promise<void> {
    if (this.finished || this.closed) return;
    this.finished = true;
    const blob = await this.stopAndCollect();
    // close()/Stop may have aborted while we were waiting/flushing — discard.
    if (this.closed) return;
    // Wall-clock ms the recorder was actually live (start -> stop), for the gated
    // FE diagnostics beacon only (PII-safe: sizes/timing/confidence, no text).
    const capturedMs = Date.now() - this.startedAt;
    if (!blob || blob.size === 0) {
      // Nothing captured even after the flush + floor. Hand back an EMPTY
      // transcript (no error): the controller surfaces a general, phrase-agnostic
      // re-prompt ("didn't catch that") instead of silently idling.
      this.handlers.onSttMeta?.({ action: 'empty_no_capture', bytes: 0, mime: this.mimeType, capturedMs, confidence: 0, transcriptLen: 0, ...this.captureProfile });
      this.handlers.onFinal?.('');
      return;
    }
    try {
      const res = await transcribeStt(blob, this.abort.signal);
      if (this.closed) return; // aborted after the upload resolved — discard
      // Low provider confidence (reported > 0 but under the floor) => we did not
      // reliably hear the user: hand back an EMPTY transcript so the controller
      // re-prompts, instead of acting on a probable mis-hear. Confidence 0 means
      // "not reported" and is passed through unchanged.
      const conf = typeof res.confidence === 'number' ? res.confidence : 0;
      const trimmed = (res.transcript ?? '').trim();
      if (conf > 0 && conf < LOW_STT_CONFIDENCE) {
        // A real transcript came back BELOW the confidence floor: the adapter
        // DROPS it (re-prompt) so a probable mis-hear never acts. Beacon the drop
        // so it is VISIBLE on the owner's retest (was a silent cause of retries).
        this.handlers.onSttMeta?.({ action: 'reprompt_low_confidence', bytes: blob.size, mime: this.mimeType, capturedMs, confidence: conf, transcriptLen: trimmed.length, ...this.captureProfile });
        this.handlers.onFinal?.('');
        return;
      }
      this.handlers.onSttMeta?.({ action: 'transcribed', bytes: blob.size, mime: this.mimeType, capturedMs, confidence: conf, transcriptLen: trimmed.length, ...this.captureProfile });
      this.handlers.onFinal?.(trimmed);
    } catch (err) {
      // An intentional close()/Stop aborts the in-flight upload — swallow it
      // silently (no onFinal, no onError) so a stopped turn never resurfaces.
      if (this.closed || (err as Error)?.name === 'AbortError') return;
      if (err instanceof VoiceUnavailableError) {
        this.handlers.onError?.(new Error('Voice input is currently unavailable.'));
      } else {
        this.handlers.onError?.(err instanceof Error ? err : new Error('Could not transcribe your voice.'));
      }
    }
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    // Abort any in-flight STT upload (a finish() already running) so a late
    // transcript can never fire onFinal after Stop.
    try { this.abort.abort(); } catch { /* no-op */ }
    // Cancel a pending minimum-capture floor so Stop/cancel is instant.
    if (this.floorTimer !== null) {
      clearTimeout(this.floorTimer);
      this.floorTimer = null;
    }
    // Unblock a floor-delayed finish() that is awaiting collection (it will see
    // `closed` and discard without transcribing).
    const resolve = this.pendingCollectResolve;
    this.pendingCollectResolve = null;
    try {
      if (this.recorder.state !== 'inactive') {
        this.recorder.onstop = null;
        this.recorder.stop();
      }
    } catch {
      /* no-op */
    }
    // Discard the captured audio so the blob/chunks are never transcribed.
    this.chunks.length = 0;
    resolve?.(null);
  }
}

export class HttpSttAdapter implements SttAdapter {
  readonly provider: string;
  private readonly minCaptureMs: number;
  private readonly tailHoldMs: number;
  private readonly captureReadyTimeoutMs: number;

  constructor(provider: string, opts?: { minCaptureMs?: number; tailHoldMs?: number; captureReadyTimeoutMs?: number }) {
    this.provider = provider;
    this.minCaptureMs = opts?.minCaptureMs ?? DEFAULT_MIN_CAPTURE_MS;
    this.tailHoldMs = opts?.tailHoldMs ?? DEFAULT_TAIL_HOLD_MS;
    this.captureReadyTimeoutMs = opts?.captureReadyTimeoutMs ?? DEFAULT_CAPTURE_READY_TIMEOUT_MS;
  }

  async openStream(opts: SttOpenOptions, handlers: SttStreamHandlers): Promise<SttStream> {
    const stream = opts.mediaStream;
    const MR = (globalThis as unknown as { MediaRecorder?: unknown }).MediaRecorder as
      | (new (s: MediaStream, o?: MediaRecorderOptions) => MediaRecorder)
      | undefined;
    if (!stream || typeof MR === 'undefined') {
      throw new VoiceUnavailableError('This browser cannot record audio for voice input.');
    }
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MR(stream, { mimeType }) : new MR(stream);
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    // Start recording with NO timeslice so the ENTIRE utterance is emitted as a
    // SINGLE finalized container (decodable on iOS mp4 AND desktop webm). A
    // timeslice fragments iOS Safari `audio/mp4` into non-concatenable pieces, so
    // concatenating them yields a clip where only the first fragment decodes —
    // the first-word truncation. stop() alone on finish() flushes the one
    // clip WITHOUT a requestData() pre-flush (which mis-dated the container and
    // degraded Deepgram accuracy), and the min-capture floor keeps an ultra-short
    // tap from a 0-byte blob.
    // A hold is bounded by the controller record cap, so one un-sliced buffer
    // stays small. All `dataavailable` chunks are still assembled into ONE Blob
    // on onstop (one POST at finish()).
    //
    // CAPTURE-READY GATE (2026-07-22): start the recorder and DO NOT resolve
    // openStream until it has ACTUALLY begun capturing (its `start` event). The
    // controller shows "Listening" / invites speech only AFTER openStream
    // resolves, so the user now speaks into a LIVE recorder. Previously this
    // resolved the instant start() was CALLED, so on mobile the mic warm-up
    // (esp. a first-permission grant) dropped the user's LEADING words.
    const startLatencyMs = await startRecorderAndAwaitCapture(recorder, this.captureReadyTimeoutMs);
    // startedAt is the moment capture is genuinely LIVE (not the start() call),
    // so the minimum-capture floor measures REAL recorded time.
    const startedAt = Date.now();
    // Capture parameters read at CAPTURE TIME (gated FE diagnostics only): the
    // exact recorder mime/codec, the mic track getSettings() (AGC/gain/sample-rate
    // that differ mobile-vs-desktop), the configured bitrate, a coarse surface
    // label and the recorder-start latency. Text-free; never alters behaviour.
    const captureProfile = readCaptureProfile(stream, recorder, startLatencyMs);
    // Per-capture correlation id (one per utterance): forwarded to the backend STT
    // upload (X-BC-Voice-Cid) AND emitted on the FE stt_result beacon so the FE
    // capture line, the backend ffprobe record and the Deepgram stt.result all JOIN
    // on ONE identical cid. Text-free; never changes capture/transcription behaviour.
    captureProfile.cid = newCaptureCid();
    return new HttpSttStream(
      this.provider,
      recorder,
      chunks,
      mimeType ?? (recorder.mimeType || 'audio/webm'),
      handlers,
      startedAt,
      this.minCaptureMs,
      this.tailHoldMs,
      captureProfile,
    );
  }
}

export { HttpSttStream };
